import { useEffect, useRef, useState } from 'react';
import styles from '../../MagicUpload/components/FilteredHeader/FilteredHeader.module.scss';
import bottomAngleIcon from '../../../assets/bottomAngleIcon.svg';
import { truncateByCharacters } from '../../../utils/utils';
import { useOutsideClick } from '../../../utils/constants/hooks';

type Option = {
    label: string;
    value: string;
    type?: string;
};

type DropdownProps = {
    options: Option[];
    disabledOptions: string[];
    selectedItems: string[];
    placeholder?: string;
    toggleSelection: (selected: string[]) => void;
    closeDropdown?: (options: Option[]) => void;
    showInput?: boolean;
    dropdownStyle?: React.CSSProperties;
};

const MultiSelectWithDisabledOptions = ({
    options,
    disabledOptions,
    selectedItems,
    placeholder = '',
    toggleSelection,
    closeDropdown,
    showInput = true,
    dropdownStyle,
}: DropdownProps) => {
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState<Option[]>([]);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const latestOptionsRef = useRef<Option[]>([]);

    useEffect(() => {
        latestOptionsRef.current = options;
    }, [options]);

    const mergedSelectedItems = Array.from(new Set([...disabledOptions, ...selectedItems]));
    const isChecked = (value: string) => mergedSelectedItems.includes(value);
    const isDisabled = (value: string) => disabledOptions.includes(value);

    // Filter options based on search term
    useEffect(() => {
        if (search) {
            setFilteredOptions(options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase())));
        } else {
            setFilteredOptions(options);
        }
    }, [search, options]);

    // Close dropdown when clicking outside
    useOutsideClick(dropdownRef, () => {
        setOpen(false);
        if (closeDropdown) {
            closeDropdown(latestOptionsRef.current);
        }
    });

    useEffect(() => {
        if (!showInput) {
            setOpen(true);
        }
    }, [showInput]);

    const handleSelection = (option: Option) => {
        if (isDisabled(option.value)) return;

        if (isChecked(option.value)) {
            toggleSelection(selectedItems.filter((val) => val !== option.value));
        } else {
            toggleSelection([...selectedItems, option.value]);
        }
    };

    const handleSelectAll = () => {
        const enabledFilteredValues = filteredOptions.filter((opt) => !isDisabled(opt.value)).map((opt) => opt.value);

        const allSelected = enabledFilteredValues.every((val) => selectedItems.includes(val));

        if (allSelected) {
            toggleSelection(selectedItems.filter((val) => !enabledFilteredValues.includes(val)));
        } else {
            toggleSelection(Array.from(new Set([...selectedItems, ...enabledFilteredValues])));
        }
    };

    const handleClearAll = () => {
        toggleSelection([...disabledOptions]); // Keep only disabled selected
    };

    // Group options so that disabled ones appear on top
    const sortedOptions = [
        ...filteredOptions.filter((opt) => isDisabled(opt.value)),
        ...filteredOptions.filter((opt) => !isDisabled(opt.value)),
    ];

    /**
     * Render the header part of the component showing selected items or placeholder and caret icon.
     * @returns {JSX.Element} Header JSX.
     */
    const renderHeader = (): JSX.Element => {
        return mergedSelectedItems.length > 0 ? (
            <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1ch' }}>
                    <span className={styles.FloatingLabel}>{placeholder}</span>
                    <span
                        style={{
                            background: 'var(--secondary-bg-colour)',
                            color: 'var(--primary-text-colour)',
                            maxWidth: '10rem',
                            display: 'flow',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                        className={styles.SelectedItem}
                    >
                        {options.find((option) => option.value === mergedSelectedItems[0])?.label}
                    </span>
                    {mergedSelectedItems.length > 1 && <span className={styles.AndMore}>+{mergedSelectedItems.length - 1} More</span>}
                </div>
                <img
                    className={styles.DownArrow}
                    src={bottomAngleIcon}
                    alt="caret"
                    style={{
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                    }}
                />
            </>
        ) : (
            <>
                <span className={styles.PlaceholderText}>{placeholder}</span>
                <img className={styles.DownArrow} src={bottomAngleIcon} alt="caret" />
            </>
        );
    };

    /**
     * Renders dropdown related sections based on provided section key.
     * @param {string} section - One of 'searchInput', 'selectClearControls', or 'optionsList'.
     * @returns {JSX.Element | null} JSX for the section or null if unknown.
     */
    const renderDropdownSection = (section: string): JSX.Element | null => {
        switch (section) {
            case 'searchInput':
                return (
                    <input
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={styles.SearchInputFilter}
                        onClick={(e) => e.stopPropagation()}
                    />
                );

            case 'selectClearControls':
                return (
                    <div
                        className={styles.SelectAllWrapper}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAll();
                        }}
                    >
                        <div className={styles.SelectAllCheckbox}>
                            <input
                                type="checkbox"
                                checked={filteredOptions
                                    .filter((opt) => !isDisabled(opt.value))
                                    .every((opt) => selectedItems.includes(opt.value))}
                                onChange={handleSelectAll}
                                onClick={(e) => e.stopPropagation()}
                            />
                            Select All
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClearAll();
                            }}
                        >
                            Clear All
                        </button>
                    </div>
                );

            case 'optionsList':
                return (
                    <div className={styles.FilteredDropdownWrapper} style={{ maxHeight: '450px', overflowY: 'auto' }}>
                        {sortedOptions.length > 0 ? (
                            sortedOptions.map((opt) => (
                                <div
                                    key={opt.value}
                                    className={`${isDisabled(opt.value) ? styles.DisabledItem : ''} ${styles.FilteredDropdownItem}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelection(opt);
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked(opt.value)}
                                        disabled={isDisabled(opt.value)}
                                        readOnly
                                        onChange={() => handleSelection(opt)}
                                    />
                                    <label className={styles.DropdownLabel} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{truncateByCharacters(opt.label || '', 15)}</span>
                                        <span style={{ fontSize: '0.8em', color: '#888' }}>{truncateByCharacters(opt.type || '', 15)}</span>
                                    </label>
                                </div>
                            ))
                        ) : (
                            <div className={styles.NoData}>
                                <span>No Results</span>
                            </div>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <>
            {showInput ? (
                <div
                    className={styles.SearchBar}
                    onClick={() => setOpen(!open)}
                    style={
                        mergedSelectedItems.length > 0
                            ? {
                                  width: '100%',
                                  background: 'white',
                                  border: '0.5px solid #c7c7c7',
                                  height: '44px',
                              }
                            : { width: '100%', height: '44px' }
                    }
                    ref={dropdownRef}
                >
                    {renderHeader()}
                    {open && (
                        <div
                            className={styles.FilteredDropdownContainer}
                            style={{ width: 'var(--dropdown-width, 100%)', overflow: 'auto' }}
                        >
                            {renderDropdownSection('searchInput')}
                            {renderDropdownSection('selectClearControls')}
                            {renderDropdownSection('optionsList')}
                        </div>
                    )}
                </div>
            ) : (
                open && (
                    <div ref={dropdownRef} onClick={() => setOpen(!open)}>
                        <div
                            className={styles.FilteredDropdownContainer}
                            style={{
                                width: 'var(--dropdown-width, 100%)',
                                overflow: 'auto',
                                position: 'absolute',
                                top: '100%',
                                left: '0',
                                ...dropdownStyle,
                            }}
                        >
                            {renderDropdownSection('searchInput')}
                            {renderDropdownSection('selectClearControls')}
                            {renderDropdownSection('optionsList')}
                        </div>
                    </div>
                )
            )}
        </>
    );
};

export default MultiSelectWithDisabledOptions;
